import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { ExternalLinkIcon } from 'lucide-react'

import {
  AirdropEligibilityBreakdown,
  AirdropEligibilityBreakdownProps,
} from '../components/airdrop-eligibility-breakdown/AirdropEligibilityBreakdown'
import { DashboardSidePanel } from '../components/dashboard-side-panel/DashboardSidePanel'
import { Token } from '@/domain/types/Token'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui/atoms/collapsible/Collapsible'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { links } from '@/ui/constants/links'
import { PageLayout } from '@/ui/layouts/PageLayout'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface DashboardViewProps {
  spk: Token
  alreadyClaimedAirdrop: NormalizedNumber
  airdrops: AirdropEligibilityBreakdownProps['airdrops']
  totalAllocation: NormalizedNumber
  totalClaimedAllocation: NormalizedNumber
  totalExpiredAllocation: NormalizedNumber
  stake: () => void
}

export function DashboardView({
  spk,
  alreadyClaimedAirdrop,
  airdrops,
  totalAllocation,
  totalClaimedAllocation,
  totalExpiredAllocation,
  stake,
}: DashboardViewProps) {
  return (
    <PageLayout>
      <h1 className="typography-heading-1">My Airdrop</h1>
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[7fr_3fr] xl:grid-cols-[11fr_5fr]">
        <div className="md:order-2">
          <DashboardSidePanel spk={spk} claimedAmount={alreadyClaimedAirdrop} openStakeDialog={stake} />
        </div>
        <div className="flex flex-col">
          <Panel spacing="l" className="flex flex-col gap-5 rounded-md sm:gap-8">
            <Collapsible defaultOpen>
              <CollapsibleTrigger>
                <div className="typography-label-3 sm:typography-heading-5">Your Spark Airdrop Campaign Summary</div>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-6">
                <div
                  className={cn(
                    'mb-6 flex w-fit divide-x divide-secondary/40 rounded-md border border-primary p-2',
                    '*:px-2 *:first:pl-0 *:last:pr-0 sm:px-5 sm:py-4 sm:*:px-4',
                  )}
                >
                  <div className="flex flex-col gap-1">
                    <div className="typography-label-3 text-secondary">Total allocation</div>
                    <div className="flex items-center gap-1 sm:gap-1.5">
                      <TokenIcon token={spk} className="size-4" />
                      <div className="text-primary" data-testid={testIds.spkAirdrop.dashboard.summary.totalAllocation}>
                        {spk.format(totalAllocation, { style: 'compact' })}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="typography-label-3 text-secondary">Total claimed</div>
                    <div className="flex items-center gap-1 sm:gap-1.5">
                      <TokenIcon token={spk} className="size-4" />
                      <div
                        className="text-primary"
                        data-testid={testIds.spkAirdrop.dashboard.summary.totalClaimedAllocation}
                      >
                        {spk.format(totalClaimedAllocation, { style: 'compact' })}
                      </div>
                    </div>
                  </div>
                  {totalExpiredAllocation.gt(0) && (
                    <div className="flex flex-col gap-1">
                      <div className="typography-label-3 inline-flex items-center gap-1 text-secondary">
                        Total expired{' '}
                        <Info>
                          Deadline for claiming airdrop has passed.{' '}
                          <ExternalLink
                            to={links.docs.airdrop.summary}
                            variant="underline"
                            className="inline-flex items-center gap-1"
                          >
                            Learn more <ExternalLinkIcon className="icon-size-xxs" />
                          </ExternalLink>
                          .
                        </Info>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-1.5">
                        <TokenIcon token={spk} className="size-4" />
                        <div
                          className="text-primary"
                          data-testid={testIds.spkAirdrop.dashboard.summary.totalExpiredAllocation}
                        >
                          {spk.format(totalExpiredAllocation, { style: 'compact' })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <AirdropEligibilityBreakdown airdrops={airdrops} spk={spk} />
              </CollapsibleContent>
            </Collapsible>
          </Panel>
        </div>
      </div>
    </PageLayout>
  )
}
